import styles from './Sidebar.module.css';
import { i18n } from '@lingui/core';
import { connect } from 'react-redux';
import { onChangeRegions } from '../../ac/onChangeRegions';
import { onSignOut } from '../../ac/onSignOut';
import { regions } from '../../constants/regions';
import { LayersGroup } from './LayersGroup';
import { UserSettings } from './UserSettings';
import { groupRegionsByCountry } from '../../utils/fetchRegions';
import { isLayerGroupHidden } from '../../utils/userSettings';
import { useState, useMemo } from 'react';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Popper from '@material-ui/core/Popper';
import TextField from '@material-ui/core/TextField';
import { ButtonWithSpinner } from '../Buttons/ButtonWithSpinner';
import { useSpinner } from '../../hooks/useSpinner';
import { t } from '@lingui/macro';

const SidebarComponent = ({ layers, selectedRegion, onChangeRegions, isVisible, apiRegions, onSignOut, userSettings }) => {
    const [ search, setSearch ] = useState('');
    const [ isFocused, setIsFocused ] = useState(false);
    const [ isSendingRequest, onClickSignOutButton ] = useSpinner(onSignOut);
    const regionsList = apiRegions && apiRegions.length > 0 ? apiRegions : regions;
    const groupedRegions = groupRegionsByCountry(regionsList);
    const flatRegionsList = useMemo(() => {
        const arr = [];
        groupedRegions.forEach(({ country, regions: countryRegions }) => {
            countryRegions.forEach(region => {
                arr.push({ ...region, country });
            });
        });
        return arr;
    }, [ groupedRegions ]);
    const selectedOption = flatRegionsList.find(r => r.id === selectedRegion.id) || null;
    // Скрытые группы отсекаются только на отрисовке панели: в redux они остаются,
    // поэтому их слои по-прежнему доступны поиску, прямым ссылкам и остаются
    // включёнными, если пользователь включил их до того, как скрыл группу
    const visibleLayerGroups = useMemo(
        () => layers.filter(({ id }) => !isLayerGroupHidden(userSettings, id)),
        [ layers, userSettings ]
    );
    const getRegionLabel = region => {
        if (!region) return '';
        if (i18n.locale === 'en' && region.name_en) return region.name_en;
        if (typeof region.name === 'object') return i18n._(region.name);
        return region.name;
    };
    const filterOptions = createFilterOptions({
        stringify: option => {
            // Поиск по name (ru), name_en (en) и, если name — объект, по всем значениям
            let ru = '';
            if (typeof option.name === 'object') {
                ru = Object.values(option.name).join(' ');
            } else {
                ru = option.name || '';
            }
            const en = option.name_en || '';
            return `${ru} ${en}`;
        },
    });

    return (
        <div className={`${styles.mainNav} ${isVisible ? styles.sidebarVisible : styles.sidebarHidden}`}>
            <div className={styles.filterWrapper}>
                <Autocomplete
                    options={flatRegionsList}
                    groupBy={option => option.country}
                    getOptionLabel={getRegionLabel}
                    value={selectedOption}
                    onChange={(e, value) => {
                        if (value) {
                            onChangeRegions({ target: { value: value.id } });
                        }
                        setSearch('');
                        setIsFocused(false);
                        // Снимаем фокус с поля ввода
                        setTimeout(() => {
                            if (document.activeElement instanceof HTMLElement) {
                                document.activeElement.blur();
                            }
                        }, 0);
                    }}
                    inputValue={isFocused ? search : getRegionLabel(selectedOption)}
                    onInputChange={(e, value, reason) => {
                        if (reason !== 'reset') setSearch(value);
                    }}
                    filterOptions={filterOptions}
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder={
                                isFocused
                                    ? (i18n.locale === 'en' ? 'Search region...' : 'Поиск региона...')
                                    : ''
                            }
                            variant="standard"
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => {
                                // Если ничего не выбрано и не введено, вернуть отображение выбранного региона
                                setIsFocused(false);
                                setSearch('');
                            }}
                            InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                style: {
                                    color: '#fff',
                                    background: 'rgba(46, 53, 61, 0.95)',
                                    borderRadius: 6,
                                    padding: '6px 10px',
                                    fontSize: '1em',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                },
                            }}
                            inputProps={{
                                ...params.inputProps,
                                style: {
                                    color: '#fff',
                                    background: 'transparent',
                                    fontSize: '1em',
                                    padding: 0,
                                },
                            }}
                        />
                    )}
                    style={{ width: '100%', marginBottom: 8 }}
                    disableClearable
                    autoHighlight
                    disablePortal={false}
                    popupIcon={<ExpandMoreIcon className={styles.popupIndicator} />}
                    PopperComponent={props => (
                        <Popper
                            {...props}
                            style={{
                                ...props.style,
                                zIndex: 2000,
                                width: 'max-content',
                                minWidth: props.style?.width ?? 300,
                                maxWidth: 600,
                            }}
                            placement="bottom-start"
                        />
                    )}
                    ListboxProps={{
                        style: {
                            background: '#fff',
                            color: '#212529',
                            borderRadius: 6,
                            fontSize: '0.95em',
                            maxHeight: 600,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                        },
                    }}
                />


            </div>
            <div className={styles.navGrid}>
                {visibleLayerGroups.map(({ label, layers }) => layers.length ? (
                    <LayersGroup
                        key={label.id}
                        label={i18n._(label)}
                        layers={layers}
                    />
                ) : null)}
            </div>
            <div className={styles.bottomBar}>
                <UserSettings />
                <ButtonWithSpinner
                    onClick={onClickSignOutButton}
                    className={styles.logoutButton}
                    isLoading={isSendingRequest}
                >
                    {t`Выйти`}
                </ButtonWithSpinner>
            </div>
        </div>
    );
};
const mapStateToProps = ({ map: { layers, selectedRegion, apiRegions }, loggedUser: { userSettings } }) => ({
    layers,
    selectedRegion,
    apiRegions,
    userSettings,
});

export const Sidebar = connect(mapStateToProps, { onChangeRegions, onSignOut })(SidebarComponent);
